import { type URLSearchParams } from 'url';

import { CrossCircledIcon, MagnifyingGlassIcon, SewingPinFilledIcon } from '@radix-ui/react-icons';

import { useClearableField } from '~/components/forms/use-clearable-field';
import { INPUT_NO_FOCUS_HIGHLIGHT_CLASS } from '~/styles/classNames/inputs';
import { cn } from '~/styles/extended-theme';
import { type PropsWithClassName } from '~/utils/props-with-classname';

import { CityCombobox } from './city-combobox';

interface Props {
  comboboxItems: string[];
  searchParams: URLSearchParams;
  namePlaceholder?: string;
}

export function PublicSearchInput({
  comboboxItems,
  searchParams,
  namePlaceholder = 'Name',
  className,
}: PropsWithClassName<Props>) {
  const initialName = searchParams.get('search') || '';
  const nameField = useClearableField('search', initialName);

  const initialCity = searchParams.get('city') || '';
  const cityField = useClearableField('city', initialCity);

  return (
    <div
      className={cn(
        'inline-flex w-full rounded-full border border-[#0000006B] bg-white px-4 py-1.5',
        'tablet:hover:border-1 tablet:hover:border-black',
        'tablet:focus-within:border-2 tablet:focus-within:border-black',
        className,
      )}
    >
      <div
        className={cn(
          'flex w-1/2 items-center gap-2 rounded-full',
          'focus-within:border-2 tablet:focus-within:border-0',
        )}
      >
        <MagnifyingGlassIcon className="shrink-0" width={16} height={16} />
        <input
          type="text"
          id="search"
          className={cn(
            'mt-1 w-full grow bg-transparent text-subtitle2 font-light',
            INPUT_NO_FOCUS_HIGHLIGHT_CLASS,
          )}
          placeholder={namePlaceholder}
          {...nameField.inputProps}
        />
        {nameField.showClearButton ? (
          <button type="button" {...nameField.clearButtonProps}>
            <CrossCircledIcon />
          </button>
        ) : null}
      </div>

      <div className="mx-2 block h-6 w-[1px] bg-gray-400"></div>

      <div
        className={cn(
          'flex w-1/2 items-center gap-2 rounded-full',
          'focus-within:border-2 tablet:focus-within:border-0',
        )}
      >
        <SewingPinFilledIcon className="shrink-0" width={16} height={16} />
        <CityCombobox
          inputProps={{
            className: cn(
              'text-subtitle2 font-light grow mt-1 w-full',
              INPUT_NO_FOCUS_HIGHLIGHT_CLASS,
            ),
          }}
          items={comboboxItems}
          {...cityField.inputProps}
          containerClassName="flex grow"
        />
        {cityField.showClearButton ? (
          <button type="button" {...cityField.clearButtonProps}>
            <CrossCircledIcon />
          </button>
        ) : null}
      </div>
    </div>
  );
}
